import { type ButtonHTMLAttributes, type AnchorHTMLAttributes } from 'react';

type ButtonBaseProps = {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
};

type ButtonAsButton = ButtonBaseProps & ButtonHTMLAttributes<HTMLButtonElement> & { href?: undefined };
type ButtonAsLink = ButtonBaseProps & AnchorHTMLAttributes<HTMLAnchorElement> & { href: string };
type ButtonProps = ButtonAsButton | ButtonAsLink;

const sizeClasses = {
  sm: 'px-4 py-2 text-sm',
  md: 'px-6 py-3 text-base',
  lg: 'px-8 py-4 text-lg',
};

const variantClasses = {
  primary: 'bg-foreground-950 text-background-50 hover:bg-foreground-800 transition-colors duration-200',
  secondary: 'bg-secondary-500 text-background-50 hover:bg-secondary-600 transition-colors duration-200',
  outline: 'border-2 border-foreground-300 text-foreground-800 hover:border-foreground-950 hover:text-foreground-950 transition-colors duration-200',
  ghost: 'text-foreground-600 hover:text-foreground-950 hover:bg-background-100 transition-colors duration-200',
};

export default function Button({ variant = 'primary', size = 'md', className = '', ...props }: ButtonProps) {
  const classes = `inline-flex items-center justify-center rounded-full font-semibold whitespace-nowrap cursor-pointer ${sizeClasses[size]} ${variantClasses[variant]} ${className}`;

  if ('href' in props && props.href) {
    const { href, target, rel, ...rest } = props as ButtonAsLink;
    const isInternal = href.startsWith('/') || href.startsWith('#');
    const externalProps = isInternal ? {} : { target: '_blank', rel: 'noopener nofollow' };
    return <a href={href} className={classes} {...externalProps} {...rest} />;
  }

  const { ...rest } = props as ButtonAsButton;
  return <button className={classes} {...rest} />;
}